// Typed fetch client. Response types come from apps/api as type-only imports (ARCHITECTURE §2).
import type { QueryClient } from "@tanstack/react-query";
import { filtersToParams, type Filters } from "@po/shared";
import type { kpis, options, overview, posSummary, QueryCtx, requisitionsSummary, spend } from "../../../api/src/queries";
import type { TableResult } from "../../../api/src/tables";
import type { itemDetail, poDetail, positionDetail, requisitionDetail, rfqComparison, rfqDetail } from "../../../api/src/records";
import type { freshness } from "../../../api/src/app";
import type { addressDetail } from "../../../api/src/addresses";
import type { itemTreeLevel } from "../../../api/src/items";
import type { certificateTreeLevel } from "../../../api/src/certificates";
import type { companyDetail } from "../../../api/src/companies";
import type { budgetDataIssues, budgetLine, budgetsPage, projectLine } from "../../../api/src/budgets";
import type { autoCountLine, autoCountSheet } from "../../../api/src/autocount-budgets";
export type { AutoCountUserRow } from "../../../api/src/autocount-users";

type R<F extends (...a: any) => any> = Awaited<ReturnType<F>>;
export type Kpis = R<typeof kpis>;
export type Overview = R<typeof overview>;
export type ReqSummary = R<typeof requisitionsSummary>;
export type PoSummary = R<typeof posSummary>;
export type Spend = R<typeof spend>;
export type Options = R<typeof options>;
export type Freshness = R<typeof freshness>;
export type ReqDetail = NonNullable<R<typeof requisitionDetail>>;
export type PoDetail = NonNullable<R<typeof poDetail>>;
export type RfqDetail = NonNullable<R<typeof rfqDetail>>;
export type RfqComparison = NonNullable<R<typeof rfqComparison>>;
export type PositionDetail = NonNullable<R<typeof positionDetail>>;
export type ItemDetail = NonNullable<R<typeof itemDetail>>;
export type AddressDetail = NonNullable<R<typeof addressDetail>>;
export type ItemTreeLevel = NonNullable<R<typeof itemTreeLevel>>;
export type CertificateTreeLevel = NonNullable<R<typeof certificateTreeLevel>>;
export type CompanyDetail = NonNullable<R<typeof companyDetail>>;
export type Settings = QueryCtx["settings"];
export type BudgetsPage = R<typeof budgetsPage>;
export type BudgetLine = NonNullable<R<typeof budgetLine>>;
export type AutoCountSheet = NonNullable<R<typeof autoCountSheet>>;
export type AutoCountLine = NonNullable<R<typeof autoCountLine>>;
export type ProjectLine = NonNullable<R<typeof projectLine>>;
export type BudgetDataIssues = R<typeof budgetDataIssues>;
export type { TableResult };
export type Me = {
  login: string;
  name: string;
  userNo: string;
  isAdmin: boolean;
  canSeeBudgets: boolean;
  /** Which of Sertica's User, Role, UserGroup and UserRight rights the role holds (SPEC §8.12). */
  userInfoRights: string[];
  /** The Sertica role signed in with; null on sessions from before roles. */
  roleNo: string | null;
  roleName: string | null;
  superintendentVessels: string[];
  env: "staging" | "production" | "development";
  settings: Settings;
  idleExpiresAt: string;
  absoluteExpiresAt: string;
};
export type JobProgress = { done: number; total: number; step: string | null };
export type Job = { id: number; state: string; error: string | null; createdAt: string; claimedAt: string | null; finishedAt: string | null; progress?: JobProgress | null };

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: { error?: string; message?: string },
  ) {
    super(body.message ?? body.error ?? `HTTP ${status}`);
  }
}

/** Called on 401 so the app can send the user to sign-in and back to the same URL. */
let onSignedOut: () => void = () => {};
export const setSignedOutHandler = (fn: () => void) => (onSignedOut = fn);

/** Last user-driven request: only these extend the 1 h idle timer (SPEC §12). */
export let lastActivity = Date.now();
/** True while visible queries reload because "as of" moved; those reloads are background. */
export const reload = { background: false };

export async function api<T>(path: string, init: RequestInit & { background?: boolean } = {}): Promise<T> {
  const background = init.background ?? reload.background;
  init = { ...init, background };
  if (!background) lastActivity = Date.now();
  const headers = new Headers(init.headers);
  if (init.body) headers.set("Content-Type", "application/json");
  // Background requests (freshness poll, background reloads) do not extend the session (SPEC §11.2).
  if (init.background) headers.set("X-PO-Background", "1");
  const res = await fetch(`/api${path}`, { ...init, headers, credentials: "same-origin" });
  if (res.status === 401 && !path.startsWith("/auth/")) {
    onSignedOut();
    throw new ApiError(401, { error: "signed_out" });
  }
  if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
  return (await res.json()) as T;
}

export const qs = (f: Partial<Filters> & Record<string, unknown>) => {
  const p = filtersToParams(f as Filters);
  const s = p.toString();
  return s ? `?${s}` : "";
};

/** Downloads an export; the server builds the .xlsx and audits it (SPEC §10). */
export async function download(path: string) {
  const res = await fetch(`/api${path}`, { credentials: "same-origin" });
  if (res.status === 401) return onSignedOut();
  if (!res.ok) throw new ApiError(res.status, await res.json().catch(() => ({})));
  const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? "export.xlsx";
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** What opens as a record page: a requisition, PO, RFQ or address. */
export type RecordType = "requisition" | "po" | "rfq" | "address" | "company";

/** A record page's data path; its query key is `["record", path]`. */
export const recordPath = (type: RecordType, no: string) =>
  `/${{ requisition: "requisitions", po: "pos", rfq: "rfqs", address: "addresses", company: "companies" }[type]}/${encodeURIComponent(no)}`;

/** Loads a record page's data from the store ahead of the click (a row hovered), so it opens filled. */
export const prefetchRecord = (qc: QueryClient, type: RecordType, no: string) =>
  qc.prefetchQuery({ queryKey: ["record", recordPath(type, no)], queryFn: () => api(recordPath(type, no)) });
